/**
 * Base Service
 * Generic service with error handling, retry logic, and type safety
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios'
import { ApiResponse, ApiError } from '../types'
import { normalizeError, logError } from '../utils/error.utils'
import { API_CONFIG, STORAGE_KEYS } from '../constants/app.constants'
import { clearStoredSession, refreshAccessToken } from '../api/refresh-token'

export class BaseService {
  protected client: AxiosInstance

  constructor(baseURL: string = API_CONFIG.BASE_URL) {
    this.client = axios.create({
      baseURL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        logError(error, 'Request Interceptor')
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        logError(error, 'Response Interceptor')

        // A 401 may just mean the access token expired between the proactive refresh
        // and this request. Try ONE refresh and replay before ending the session —
        // logging out immediately is what made short-lived tokens painful.
        const original = error.config as
          | (AxiosRequestConfig & { _retry?: boolean })
          | undefined

        if (error.response?.status === 401 && original && !original._retry) {
          // Set before awaiting: a request must never retry twice, or a persistently
          // rejected token would ping-pong between refresh and retry.
          original._retry = true

          const outcome = await refreshAccessToken('401-retry')

          if (outcome.status === 'refreshed') {
            original.headers = {
              ...(original.headers || {}),
              Authorization: `Bearer ${outcome.token}`,
            }
            return this.client(original)
          }

          // Only a definitive 401/403 from the refresh proves the session is dead. On
          // 'unavailable' (5xx/offline) fall through and reject the ORIGINAL error with
          // the session intact — the refresh server being down is not grounds for
          // logging the admin out.
          if (outcome.status === 'revoked') {
            clearStoredSession()
            window.location.href = '/login'
          }
        }

        return Promise.reject(error)
      }
    )
  }

  /**
   * Generic GET request with retry logic
   */
  protected async get<T>(
    url: string,
    config?: AxiosRequestConfig,
    retries: number = API_CONFIG.RETRY_ATTEMPTS
  ): Promise<T> {
    try {
      const response = await this.client.get<ApiResponse<T>>(url, config)
      return this.extractData(response.data)
    } catch (error) {
      if (retries > 0 && this.shouldRetry(error)) {
        await this.delay(API_CONFIG.RETRY_DELAY)
        return this.get<T>(url, config, retries - 1)
      }
      throw this.handleError(error, `GET ${url}`)
    }
  }

  /**
   * Generic POST request
   */
  protected async post<T, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.client.post<ApiResponse<T>>(url, data, config)
      return this.extractData(response.data)
    } catch (error) {
      throw this.handleError(error, `POST ${url}`)
    }
  }

  /**
   * Generic PUT request
   */
  protected async put<T, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.client.put<ApiResponse<T>>(url, data, config)
      return this.extractData(response.data)
    } catch (error) {
      throw this.handleError(error, `PUT ${url}`)
    }
  }

  /**
   * Generic PATCH request
   */
  protected async patch<T, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.client.patch<ApiResponse<T>>(url, data, config)
      return this.extractData(response.data)
    } catch (error) {
      throw this.handleError(error, `PATCH ${url}`)
    }
  }

  /**
   * Generic DELETE request
   */
  protected async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.client.delete<ApiResponse<T>>(url, config)
      return this.extractData(response.data)
    } catch (error) {
      throw this.handleError(error, `DELETE ${url}`)
    }
  }

  /**
   * Extract data from API response
   */
  private extractData<T>(response: any): T {
    // Handle different response formats
    if (response && typeof response === 'object') {
      if ('data' in response) {
        return response.data
      }
      return response
    }
    return response
  }

  /**
   * Handle and normalize errors
   */
  private handleError(error: unknown, context: string): ApiError {
    const normalized = normalizeError(error)
    logError(error, context)
    return normalized
  }

  /**
   * Check if error should trigger a retry
   */
  private shouldRetry(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status
      // Retry on network errors or 5xx server errors
      return !status || status >= 500
    }
    return false
  }

  /**
   * Delay helper for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Build query string from parameters
   */
  protected buildQueryString(params: Record<string, any>): string {
    const queryParams = new URLSearchParams()
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value))
      }
    })
    
    const queryString = queryParams.toString()
    return queryString ? `?${queryString}` : ''
  }
}

